import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronUp, Mic, Video, Volume2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { DeviceInfo } from '../types';

interface DeviceMenuProps {
  type: 'audio' | 'video';
  microphones?: DeviceInfo[];
  speakers?: DeviceInfo[];
  cameras?: DeviceInfo[];
  selectedMic?: string | null;
  selectedSpeaker?: string | null;
  selectedCamera?: string | null;
  onSelectMic?: (deviceId: string) => void;
  onSelectSpeaker?: (deviceId: string) => void;
  onSelectCamera?: (deviceId: string) => void;
  className?: string;
  /** Whether speaker selection is supported (setSinkId) - hide selector if false */
  supportsSpeakerSelection?: boolean;
}

export function DeviceMenu({
  type,
  microphones = [],
  speakers = [],
  cameras = [],
  selectedMic,
  selectedSpeaker,
  selectedCamera,
  onSelectMic,
  onSelectSpeaker,
  onSelectCamera,
  className,
  supportsSpeakerSelection = true,
}: DeviceMenuProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const isAudio = type === 'audio';

  const handleSelect = (selectFn: ((deviceId: string) => void) | undefined) => {
    return (deviceId: string) => {
      selectFn?.(deviceId);
      setOpen(false);
    };
  };

  const menuLabel = isAudio
    ? t('videoSession.devices.selectAudioDevice', 'Select audio device')
    : t('videoSession.devices.selectVideoDevice', 'Select video device');

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger
        render={
          <Button
            variant="ghost"
            size="icon-sm"
            className={cn(
              'min-h-[44px] min-w-[28px] text-white hover:bg-white/20 hover:text-white',
              className
            )}
            title={menuLabel}
            aria-label={menuLabel}
          />
        }
      >
        <ChevronUp className="h-4 w-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="top" className="w-64">
        {isAudio ? (
          <>
            <DropdownMenuGroup>
              <DropdownMenuLabel className="flex items-center gap-1.5">
                <Mic className="h-3.5 w-3.5" />
                {t('videoSession.devices.microphone', 'Microphone')}
              </DropdownMenuLabel>
              <DeviceRadioGroup
                devices={microphones}
                selectedId={selectedMic}
                onSelect={handleSelect(onSelectMic)}
              />
            </DropdownMenuGroup>

            {supportsSpeakerSelection && (
              <>
                <DropdownMenuSeparator />

                <DropdownMenuGroup>
                  <DropdownMenuLabel className="flex items-center gap-1.5">
                    <Volume2 className="h-3.5 w-3.5" />
                    {t('videoSession.devices.speaker', 'Speaker')}
                  </DropdownMenuLabel>
                  <DeviceRadioGroup
                    devices={speakers}
                    selectedId={selectedSpeaker}
                    onSelect={handleSelect(onSelectSpeaker)}
                  />
                </DropdownMenuGroup>
              </>
            )}
          </>
        ) : (
          <DropdownMenuGroup>
            <DropdownMenuLabel className="flex items-center gap-1.5">
              <Video className="h-3.5 w-3.5" />
              {t('videoSession.devices.camera', 'Camera')}
            </DropdownMenuLabel>
            <DeviceRadioGroup
              devices={cameras}
              selectedId={selectedCamera}
              onSelect={handleSelect(onSelectCamera)}
            />
          </DropdownMenuGroup>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface DeviceRadioGroupProps {
  devices: DeviceInfo[];
  selectedId?: string | null;
  onSelect?: (deviceId: string) => void;
}

function DeviceRadioGroup({
  devices,
  selectedId,
  onSelect,
}: DeviceRadioGroupProps) {
  const { t } = useTranslation();

  if (devices.length === 0) {
    return (
      <p className="text-xs text-muted-foreground px-2 py-1.5">
        {t('videoSession.devices.noDevicesFound', 'No devices found')}
      </p>
    );
  }

  return (
    <DropdownMenuRadioGroup
      value={selectedId ?? undefined}
      onValueChange={onSelect}
    >
      {devices.map((device) => (
        <DropdownMenuRadioItem
          key={device.deviceId}
          value={device.deviceId}
          className="min-h-[44px]"
        >
          <span className="truncate">{device.label}</span>
        </DropdownMenuRadioItem>
      ))}
    </DropdownMenuRadioGroup>
  );
}
